import React from 'react'
import { BaseView } from '@/components/base'
import { LinearGradient } from '@/components/LinearGradient'
import { useTheme } from '@/hooks/useTheme'
import { responsiveAdapter } from '@/utils'

interface TransactionCurrencyAvatarProps {
  uri?: string
  path?: string
  bgColor?: string | [string, string, ...string[]]
  size?: number
  iconSize?: number
  fallbackText?: string
  containerStyle?: React.CSSProperties
  // Специальные пропсы для транзакций как в RN
  containerGradientProps?: {
    locations?: number[]
    start?: [number, number]
    end?: [number, number]
  }
}

/**
 * TransactionCurrencyAvatar компонент - точная копия RN Avatar с isCurrency=true для транзакций
 * Используется специально для отображения валют на странице деталей транзакций
 * где нужно соответствие RN версии с размером 120 и специальными градиентами
 */
export const TransactionCurrencyAvatar: React.FC<
  TransactionCurrencyAvatarProps
> = ({
  uri,
  path,
  bgColor,
  size = 120,
  iconSize = 60,
  fallbackText,
  containerStyle,
  containerGradientProps,
}) => {
  const theme = useTheme()

  // Определяем источник изображения как в RN
  const imageSource = path || uri

  // Определяем цвета градиента как в RN Avatar
  const gradientColors = Array.isArray(bgColor)
    ? bgColor
    : [bgColor || '#ccc', bgColor || '#ccc']

  // Если у нас только один цвет, дублируем его для создания градиента
  const finalColors =
    gradientColors.length === 1
      ? [gradientColors[0], gradientColors[0]]
      : gradientColors

  // Используем специальные градиентные настройки для транзакций
  const gradientStart = containerGradientProps?.start
    ? { x: containerGradientProps.start[0], y: containerGradientProps.start[1] }
    : { x: 1, y: 0 } // По умолчанию как в RN CustomTransactionAvatar

  const gradientEnd = containerGradientProps?.end
    ? { x: containerGradientProps.end[0], y: containerGradientProps.end[1] }
    : { x: 0, y: 1 } // По умолчанию как в RN CustomTransactionAvatar

  const gradientLocations = containerGradientProps?.locations || undefined

  return (
    <LinearGradient
      colors={finalColors}
      locations={gradientLocations}
      start={gradientStart}
      end={gradientEnd}
      circle
      circleSize={size}
      center
      style={containerStyle}
    >
      {imageSource ? (
        <img
          src={imageSource}
          alt={fallbackText || 'Currency'}
          style={{
            width: '100%', // Полная ширина как в RN CurrencyChildren
            height: `${responsiveAdapter(iconSize)}px`, // Используем iconSize как в RN (60px для size=120)
            objectFit: 'contain', // Как в RN: contentFit="contain"
          }}
          onError={(e) => {
            // Fallback на текст если изображение не загрузилось
            const target = e.target as HTMLImageElement
            target.style.display = 'none'
          }}
        />
      ) : (
        // Fallback на первую букву как в RN Avatar
        <div
          style={{
            fontSize: `${responsiveAdapter(40)}px`,
            fontFamily: 'semibold',
            color: theme.colors.label.buttonPrimaryEnabled,
            textAlign: 'center',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {fallbackText?.charAt(0)?.toUpperCase() || '?'}
        </div>
      )}
    </LinearGradient>
  )
}
